import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TertiaryButton } from '@JohnLewisPartnership/jl-design-system/elements/button/Button';
import Plus24PxOutlinedIcon from '@JohnLewisPartnership/jl-design-system/elements/icons-jb/Plus24PxOutlined';
import Minus24PxOutlinedIcon from '@JohnLewisPartnership/jl-design-system/elements/icons-jb/Minus24PxOutlined';

// eslint-disable-next-line import/no-unresolved
import getConfig from 'next/config';
import FacetAccordion from './facet-accordion/FacetAccordion';
import OutOfStockFilter from './out-of-stock-filter/OutOfStockFilter';
import AppliedFilters from './applied-filters/AppliedFilters';
import GridColumn from '../grid/GridColumn';
import { useFacetContext } from '../../context/FacetContext';
import styles from './Facets.module.scss';

import {
  FILTER_INTERACTION,
  dispatchImmediateAnalyticsEvent,
} from '../analytics/analyticsEvents';

import SingleItemFacet from './single-item-facet/SingleItemFacet';
import { Context as SharedFacetContext } from './facet-context';

import { useExperiencesContext } from '../experiences/Experiences';
import { useCompareContext } from '../../context/CompareContext';

const { publicRuntimeConfig: features } = getConfig();

const INITIAL_NUMBER_TO_SHOW = 5;
const MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING = 7;

export const Facets = ({
  isMobile,
  isFilterDrawer,
  isPrimaryFacetMainFacet,
}) => {
  const [isFacetListExpanded, setIsFacetListExpanded] = useState(false);

  const { isFacetLimitOn, isWiderScrollbarFlagOn, isLeftScrollbarFlagOn } =
    useExperiencesContext();

  const { numberOfOpenFacetAccordions, numberOfFiltersToShowClear } = features;

  const {
    applied,
    baseFacetId,
    facets,
    outOfStockInfo,
    showHideOutOfStockFilter,
    singleItemFacet,
    primaryFacet,
  } = useFacetContext();

  const sharedFacetContext = {
    filterCombination: applied.filterCombination,
    filterCombinationNvals: applied.filterCombinationNvals,
    isMobile,
  };

  const compare = useCompareContext();
  const isCompareBarOpen = compare.products?.length > 0;

  const facetsToShow = facets.filter((facet) => facet.show);

  const isShowMoreOrFewerVisible =
    facetsToShow.length > MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING;

  const maxFacetsToDisplay =
    !isMobile &&
    isFacetLimitOn &&
    isShowMoreOrFewerVisible &&
    !isFacetListExpanded
      ? INITIAL_NUMBER_TO_SHOW
      : facetsToShow.length;

  const isAccordionOpen = (index, facetName) => {
    if (!isPrimaryFacetMainFacet) {
      return index < numberOfOpenFacetAccordions;
    }
    return facetName === primaryFacet?.name;
  };

  const toggleShowMore = (toggleValue) => {
    dispatchImmediateAnalyticsEvent(FILTER_INTERACTION, {
      type: toggleValue ? 'show more' : 'show fewer',
    });
    setIsFacetListExpanded(toggleValue);
  };

  return (
    <SharedFacetContext.Provider value={sharedFacetContext}>
      <GridColumn
        className={classNames(styles.plp__facets, {
          [styles['plp__facets--desktop']]: !isMobile,
          [styles['plp__facets--compare-open']]: isCompareBarOpen,
          [styles['plp__facets--wider-scrollbar']]: isWiderScrollbarFlagOn,
        })}
        colLg={isMobile ? undefined : 3}
        colXl={3}
        data-test="facet-container"
      >
        <nav
          aria-label="Filters"
          data-test="component-facets-container"
          className={classNames(styles.facets, {
            [styles['facets--compare-open']]: isCompareBarOpen,
            [styles['facets--mobile']]: isMobile,
            [styles['facets--wider-scrollbar']]: isWiderScrollbarFlagOn,
            [styles['facets--left-scrollbar']]: isLeftScrollbarFlagOn,
          })}
        >
          {/* The extra container div below is only needed to make the left-hand scrollbar work.
              If the experiment fails and we get rid of left-hand scroll, this container can be removed
              and the data-test component-facets can return to the nav element above. */}
          <div
            data-test="component-facets"
            className={classNames(styles.facets__contents, {
              [styles['facets__contents--left-scrollbar']]:
                isLeftScrollbarFlagOn,
            })}
          >
            {!isMobile && <h2 className="visuallyHidden">Filters</h2>}
            <AppliedFilters
              applied={applied}
              baseFacetId={baseFacetId}
              numberOfFiltersToShowClear={numberOfFiltersToShowClear}
              showClearFiltersLink={!isMobile}
              mobile={isMobile}
              isFilterDrawer={isFilterDrawer}
            />
            <OutOfStockFilter
              showHideOutOfStockFilter={showHideOutOfStockFilter}
              outOfStockInfo={outOfStockInfo}
            />
            <SingleItemFacet facet={singleItemFacet} />
            {facetsToShow.map((facet, index) => (
              <FacetAccordion
                href="/"
                key={facet.dimensionName}
                opened={isAccordionOpen(index, facet.name)}
                facet={facet}
                showColourFilterSwatches
                isFilterDrawer={isFilterDrawer}
                position={index + 1}
                isShown={
                  isMobile ||
                  !isFacetLimitOn ||
                  isFacetListExpanded ||
                  index < maxFacetsToDisplay
                }
              />
            ))}
            {!isMobile && isFacetLimitOn && isShowMoreOrFewerVisible && (
              <TertiaryButton
                data-test="toggle-more-facets"
                onClick={() => toggleShowMore(!isFacetListExpanded)}
                className={styles['toggle-more-facets']}
                small={true}
              >
                <span className={styles.showMoreButton}>
                  {isFacetListExpanded ? (
                    <Minus24PxOutlinedIcon className={styles.marginRight} />
                  ) : (
                    <Plus24PxOutlinedIcon className={styles.marginRight} />
                  )}
                  Show {isFacetListExpanded ? 'fewer' : 'more'} filters
                </span>
              </TertiaryButton>
            )}
          </div>
        </nav>
      </GridColumn>
    </SharedFacetContext.Provider>
  );
};

Facets.defaultProps = {
  isMobile: false,
  isFilterDrawer: false,
  isPrimaryFacetMainFacet: false,
};

Facets.propTypes = {
  isMobile: PropTypes.bool,
  isFilterDrawer: PropTypes.bool,
  isPrimaryFacetMainFacet: PropTypes.bool,
};

export default Facets;
